import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { getEnabledQuickLinkPages, getEnabledLegalPages, isPageEnabled } from '../config/siteConfig'
import './Footer.css'

const Footer = () => {
  const currentYear = new Date().getFullYear()

  // Get enabled pages from config
  const quickLinkPages = useMemo(() => {
    const pages = getEnabledQuickLinkPages()
    // Add Home at the beginning if enabled
    if (isPageEnabled('home')) {
      return [{ key: 'home', path: '/', title: 'Home' }, ...pages]
    }
    return pages
  }, [])

  const legalPages = useMemo(() => {
    const pages = getEnabledLegalPages()
    // Add Quality Standards link if quality page is enabled
    if (isPageEnabled('quality')) {
      return [...pages, { key: 'quality-standards', path: '/quality', title: 'Quality Standards' }]
    }
    return pages
  }, [])

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          {/* Company Info */}
          <div className="footer-brand">
            <Link to="/" className="footer-logo">
              <img 
                src="/assets/images/logo.png" 
                alt="SIBR Lifescience" 
                className="footer-logo-image"
              />
              <div className="footer-logo-text">
                <span className="footer-logo-name">SIBR Lifescience</span>
                <span className="footer-logo-byline">Provides Healthy Life & Care with Quality</span>
              </div>
            </Link>
            <p className="footer-description">
              Committed to improving healthcare through quality pharmaceutical products. 
              GMP certified manufacturing with unwavering commitment to patient safety.
            </p>
            <a 
              href="https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-cta"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              Find Our Products on 1mg
            </a>
          </div>

          {/* Quick Links */}
          <div className="footer-links">
            <h4>Quick Links</h4>
            <ul>
              {quickLinkPages.map((page) => (
                <li key={page.key}>
                  <Link to={page.path}>{page.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div className="footer-links">
            <h4>Legal</h4>
            <ul>
              {legalPages.map((page) => (
                <li key={page.key}>
                  <Link to={page.path}>{page.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact NAP */}
          <div className="footer-contact">
            <h4>Contact Us</h4>
            
            {/* Registered Office */}
            <div className="contact-item address-item">
              <div className="contact-label-row">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="contact-icon">
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                </svg>
                <span className="contact-label">Registered At</span>
              </div>
              <address>
                <strong>SIBR Life Sciences Pvt. Ltd.</strong><br />
                Shanti Niketan, Shop 102/B Raj Complex,<br />
                Satyanarayan Park Main Rd,<br />
                Rajkot, Gujarat 360007
              </address>
            </div>

            {/* Manufacturing */}
            <div className="contact-item address-item">
              <div className="contact-label-row">
                <svg viewBox="0 0 24 24" fill="currentColor" className="contact-icon">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                <span className="contact-label">Manufactured At</span>
              </div>
              <address>
                Industrial Area, Baddi<br />
                District Solan<br />
                Himachal Pradesh - 173205
              </address>
            </div>

            <div className="contact-item email-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="contact-icon">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              <a href="mailto:info@sibrlifescience.com">info@sibrlifescience.com</a>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <p>© {currentYear} SIBR Life Sciences Private Limited. All rights reserved.</p>
          <p className="footer-disclaimer">
            This website is intended for informational purposes only. 
            Please consult your healthcare provider before taking any medication.
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
